import "./style.scss";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { LoadingButton } from "@mui/lab";
import NoBooksIcon from "assets/img/svg/noBooks";
import { Tooltip } from "components/lib/Tooltip";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import { blackButtonStyles } from "components/shared/StyledButton";
import StyledCheckbox from "components/shared/StyledCheckbox";
import { useTranslation } from "react-i18next";

import type { TCheckInfo, TDilicomInfo } from "../types";

interface IDilicomModal {
    dilicomInfo: TDilicomInfo;
    ean13Value: string;
    onCheckInfo: (name: string, value: boolean) => void;
    checkInfo: TCheckInfo;
    onSubmitAutoCompletion: () => void;
}

/**
 * This modal component show Dilicom details data about a Title
 * @param {object of strings} dilicomInfo Title data from Dilicom
 * @param {string} ean13Value ean13 input value from the watch props controller hook
 * @param {object of boolean} initialCheckedInfo initial state of checkInfo
 * @param {VoidFunction} onCheckInfo function to change state of checkInfo
 * @param {VoidFunction} onSubmitAutoCompletion validate informations function
 */

const DilicomInformations = ({
    dilicomInfo,
    ean13Value,
    onCheckInfo,
    checkInfo,
    onSubmitAutoCompletion,
}: IDilicomModal) => {
    const [t] = useTranslation();

    const checkBoxStyles = {
        color: "#B2B2B2",
        "&.Mui-checked": {
            color: "#275AB0 !important",
        },
        ".MuiSvgIcon-root": {
            fontSize: "1.3rem",
        },
        "&:hover": {
            backgroundColor: "#2759b019 !important",
        },
    };

    return (
        <div className="dilicom-details-container">
            <div className="upper-details-container">
                {dilicomInfo.avatarUrl ? (
                    <div className="image-container">
                        <div className="check-box-image-container">
                            <Tooltip title={t("NewTitle.ValidateImage")} placement="top" arrow disableInteractive>
                                <StyledCheckbox
                                    defaultChecked
                                    sx={checkBoxStyles}
                                    style={{
                                        position: "absolute",
                                        zIndex: 2,
                                        right: 0,
                                        bottom: 5,
                                    }}
                                    onChange={(e) => onCheckInfo("avatarUrl", e.target.checked)}
                                />
                            </Tooltip>
                        </div>
                        <img
                            className={`titleCover ${checkInfo.avatarUrl ? "image-checked" : "image-non-checked"}`}
                            src={dilicomInfo.avatarUrl}
                        />
                    </div>
                ) : (
                    <div className="titleCover">
                        <NoBooksIcon />
                    </div>
                )}
                <div className="title-synopsis-container">
                    <span className="details-info">
                        <InfoOutlinedIcon style={{ height: 12, width: 12 }} />
                        {t("NewTitle.DetailsInfos")}
                    </span>
                    <div className="titleCheckboxContainer">
                        <h1 className={checkInfo.name ? "info-active" : "info"}>{dilicomInfo.name}</h1>
                        <Tooltip title={t("NewTitle.ValidateTitle")} placement="top" arrow disableInteractive>
                            <StyledCheckbox
                                defaultChecked
                                sx={checkBoxStyles}
                                onChange={(e) => onCheckInfo("name", e.target.checked)}
                            />
                        </Tooltip>
                    </div>
                    <div className="synopsisCheckboxContainer">
                        <span className={checkInfo.summary ? "info-active" : "info"}>{t("NewTitle.Summary")}</span>
                        <Tooltip title={t("NewTitle.ValidateSummary")} placement="top" arrow disableInteractive>
                            <StyledCheckbox
                                defaultChecked
                                sx={checkBoxStyles}
                                onChange={(e) => onCheckInfo("summary", e.target.checked)}
                            />
                        </Tooltip>
                    </div>
                    <ScrollShadowWrapper className="resume-scroll-shadow-wrapper">
                        <p className={`synopsis-text ${checkInfo.summary ? "info-active" : "info"}`}>
                            {dilicomInfo.summary || "-"}
                        </p>
                    </ScrollShadowWrapper>
                </div>
            </div>
            <div className="bottom-details-container">
                <div className="specifications-container">
                    <div className="details-row" style={{ background: "rgba(0, 0, 0, 0.03)" }}>
                        <span className={checkInfo.price ? "info-active" : "info"}>{t("NewTitle.Price")}</span>
                        <span className={checkInfo.price ? "info-active" : "info"}>
                            {dilicomInfo.price ? `${dilicomInfo.price}€` : "-"}
                        </span>
                        <Tooltip title={t("NewTitle.ValidatePrice")} placement="top" arrow disableInteractive>
                            <StyledCheckbox
                                defaultChecked
                                sx={checkBoxStyles}
                                onChange={(e) => onCheckInfo("price", e.target.checked)}
                            />
                        </Tooltip>
                    </div>
                    <div className="details-row">
                        <span className={checkInfo.format ? "info-active" : "info"}>{t("NewTitle.Format")}</span>
                        <span className={checkInfo.format ? "info-active" : "info"}>{dilicomInfo.format || "-"}</span>
                        <Tooltip title={t("NewTitle.ValidateFormat")} placement="top" arrow disableInteractive>
                            <StyledCheckbox
                                defaultChecked
                                sx={checkBoxStyles}
                                onChange={(e) => onCheckInfo("format", e.target.checked)}
                            />
                        </Tooltip>
                    </div>
                    <div className="details-row" style={{ background: "rgba(0, 0, 0, 0.03)" }}>
                        <span className={checkInfo.pagination ? "info-active" : "info"}>
                            {t("NewTitle.PageNumber")}
                        </span>
                        <span className={checkInfo.pagination ? "info-active" : "info"}>
                            {dilicomInfo.pagination || "-"}
                        </span>
                        <Tooltip title={t("NewTitle.ValidatePageNumber")} placement="top" arrow disableInteractive>
                            <StyledCheckbox
                                defaultChecked
                                sx={checkBoxStyles}
                                onChange={(e) => onCheckInfo("pagination", e.target.checked)}
                            />
                        </Tooltip>
                    </div>
                    <div className="details-row">
                        <span className={checkInfo.releaseDate ? "info-active" : "info"}>
                            {t("NewTitle.ReleaseDate")}
                        </span>
                        <span className={checkInfo.releaseDate ? "info-active" : "info"}>
                            {dilicomInfo.releaseDate || "-"}
                        </span>
                        <Tooltip title={t("NewTitle.ValidateReleaseDate")} placement="top" arrow disableInteractive>
                            <StyledCheckbox
                                defaultChecked
                                sx={checkBoxStyles}
                                onChange={(e) => onCheckInfo("releaseDate", e.target.checked)}
                            />
                        </Tooltip>
                    </div>
                    <div className="details-row" style={{ background: "rgba(0, 0, 0, 0.02)" }}>
                        <span className={checkInfo.category ? "info-active" : "info"}>{t("Titles.Collection")}</span>
                        <span className={checkInfo.category ? "info-active" : "info"}>
                            {dilicomInfo.category || "-"}
                        </span>
                        <Tooltip title={t("NewTitle.ValidateCollection")} placement="top" arrow disableInteractive>
                            <StyledCheckbox
                                defaultChecked
                                sx={checkBoxStyles}
                                onChange={(e) => onCheckInfo("category", e.target.checked)}
                            />
                        </Tooltip>
                    </div>
                </div>
                <div className="button-part-container">
                    <div className="ean-container">
                        <span>EAN13</span>
                        <span>{ean13Value}</span>
                    </div>
                    <LoadingButton
                        onClick={() => onSubmitAutoCompletion()}
                        sx={{
                            ...blackButtonStyles,
                            gap: "10px",
                            textTransform: "none",
                            fontFamily: "Poppins",
                            fontWeight: "400",
                        }}
                        style={{ width: 179 }}
                        fullWidth
                    >
                        {t("NewTitle.Validate")}
                    </LoadingButton>
                </div>
            </div>
        </div>
    );
};

export default DilicomInformations;
